import type { MapId } from '~/_shared/types/map';
import { removeNullsy } from '~/_shared/utils/object/pickBy';
import type {
  BoundaryGroupId, BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';
import type { DemographicIdCode } from '~/store/boundaryGroups/boundaryGroups.repository';
import {
  BoundaryTerritoryGroupConnectedEntities, BoundaryTerritoryType,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  convertBTGMatchupRequestToResponse, convertBTGSettingsRequestToResponse,
  createTerritoryBoundaryGroupModelFromResponse,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import type { BoundaryTerritoryGroupModelResponse } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import type {
  BoundaryTerritoryGroup, BoundaryTerritoryGroupSettings, BoundaryTerritoryLabelStyle,
  BoundaryTerritoryLabelValueConfig,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import type {
  DataLayerGroup, DataLayerGroupId, DataLayerIndividualSettings,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import {
  type CreateBoundaryTerritoryGroupParams, createBoundaryTerritoryGroupRequestParams,
} from '../boundary/boundaryTerritoryGroup.helpers';

export const DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES = 5;

export const isDataLayersArtificialBoundaryTerritoryGroup = (boundaryTerritoryGroup: BoundaryTerritoryGroup): boolean => {
  return boundaryTerritoryGroup.settings.connectedEntityData?.entity === BoundaryTerritoryGroupConnectedEntities.DataLayer;
};

type IndividualLayerSettingsToBTGSettingResult = Omit<Partial<BoundaryTerritoryGroupSettings>, 'style'> & {
  style?: Pick<BoundaryTerritoryGroupSettings['style'], 'gradientColors'>;
};

export const individualLayerSettingsToBTGSettings = (individualLayerSettings: Partial<DataLayerIndividualSettings>): {
  individualSettingsActive: boolean;
  btgSettings: IndividualLayerSettingsToBTGSettingResult;
} => {
  return {
    individualSettingsActive: !!individualLayerSettings.individualSettingsActive,
    btgSettings: (individualLayerSettings.boundarySettings ?
      removeNullsy({
        rangesCount: individualLayerSettings.boundarySettings.numberOfRanges,
        style: individualLayerSettings.boundarySettings.style,
        boundaryTerritories: individualLayerSettings.boundarySettings.boundaryTerritories,
      } satisfies Partial<IndividualLayerSettingsToBTGSettingResult>) :
      {}),
  };
};

export const createDataLayerDemographicBTGRequestParams = ({
  mapId,
  dataLayerGroupId,
  boundaryGroupId,
  boundaryTerritoryGroupId,
  demographic,
  numberOfRanges,
  metrics,
  style,
  boundaryTerritories,
  disableMapInteraction,
  hideLabels,
  labelStyle,
  labelValueConfig,
}: Readonly<{
  dataLayerGroupId: DataLayerGroupId;
  mapId: MapId;
  boundaryGroupId: BoundaryGroupId;
  boundaryTerritoryGroupId?: BoundaryTerritoryGroupId;
  demographic: DemographicIdCode;
  numberOfRanges?: number;
  metrics: CreateBoundaryTerritoryGroupParams['metrics'];
  style?: CreateBoundaryTerritoryGroupParams['style'];
  boundaryTerritories?: CreateBoundaryTerritoryGroupParams['boundaryTerritories'];
  disableMapInteraction?: boolean;
  hideLabels?: boolean;
  labelStyle?: BoundaryTerritoryLabelStyle;
  labelValueConfig?: BoundaryTerritoryLabelValueConfig;
}>) => ({
  boundaryTerritoryGroupId,
  ...createBoundaryTerritoryGroupRequestParams({
    mapId,
    boundaryGroupId,
    territoryType: BoundaryTerritoryType.Demographic,
    demographicId: demographic,
    numberOfRanges,
    connectedEntityData: {
      entity: BoundaryTerritoryGroupConnectedEntities.DataLayer,
      id: dataLayerGroupId,
    },
    metrics,
    style,
    boundaryTerritories,
    disableMapInteraction,
    hideLabels,
    labelStyle,
    labelValueConfig,
  }),
});

export const createSampleBTGForDataLayer = ({
  dataLayerGroup,
  boundaryGroupId,
  numberOfRanges,
}: {
  dataLayerGroup: DataLayerGroup;
  boundaryGroupId: BoundaryGroupId;
  numberOfRanges?: number;
}): BoundaryTerritoryGroup => {
  const sampleMapId = 0;

  const request = createDataLayerDemographicBTGRequestParams({
    dataLayerGroupId: dataLayerGroup.id,
    mapId: sampleMapId,
    boundaryGroupId,
    demographic: 'sample-demographic-id',
    numberOfRanges: numberOfRanges ?? dataLayerGroup.settings.numberOfRanges ?? DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES,
    metrics: [],
    style: dataLayerGroup.settings.style,
    disableMapInteraction: dataLayerGroup.settings.disableMapInteraction,
    hideLabels: dataLayerGroup.settings.hideLabels,
    labelStyle: dataLayerGroup.settings.labelStyle,
    labelValueConfig: dataLayerGroup.settings.labelValueConfig,
  });

  const itemResponse: BoundaryTerritoryGroupModelResponse = {
    map_id: 0,
    boundary_group_id: 0,
    archived_at: null,
    matchings: convertBTGMatchupRequestToResponse(request.matchings),
    settings: convertBTGSettingsRequestToResponse(request.settings, []),
  };

  return {
    ...createTerritoryBoundaryGroupModelFromResponse(itemResponse),
    boundaryTerritoryGroupId: 0,
  };
};
